import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  format as dateFnsFormat,
  isValid as dateFnsIsValid,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  formatDistanceToNow,
  getDate,
  getDay,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameYear,
  max,
  min,
  parse,
  parseISO,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subSeconds,
  subWeeks,
  subYears
} from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

// Moment format string → date-fns format string
const FORMAT_MAP = {
  YYYY: 'yyyy',
  YY: 'yy',
  DD: 'dd',
  D: 'd',
  dddd: 'EEEE',
  ddd: 'EEE',
  dd: 'EEEEEE',
  Do: 'do',
  HH: 'HH',
  hh: 'hh',
  h: 'h',
  mm: 'mm',
  ss: 'ss',
  A: 'a',
  a: 'aaa',
  z: 'zzz',
  Z: 'xxx',
  X: 't',
  x: 'T',
  LTS: 'pp',
  L: 'P'
};

/**
 * Convert a moment.js format string to a date-fns format string.
 * Handles quoted/escaped text in square brackets (moment) → single quotes (date-fns).
 */
export const convertMomentFormat = (momentFormat) => {
  if (!momentFormat) return momentFormat;

  // Replace bracket-escaped text with single-quote-escaped text
  const result = momentFormat.replace(/\[([^\]]*)\]/g, "'$1'");

  // Sort tokens by length (longest first) to avoid partial replacements
  const tokens = Object.keys(FORMAT_MAP).sort((a, b) => b.length - a.length);

  // Track quoted sections to avoid replacing inside them
  const parts = [];
  let i = 0;
  while (i < result.length) {
    if (result[i] === "'") {
      const end = result.indexOf("'", i + 1);
      if (end === -1) {
        parts.push({ text: result.slice(i), quoted: true });
        break;
      }
      parts.push({ text: result.slice(i, end + 1), quoted: true });
      i = end + 1;
    } else {
      let matched = false;
      for (const token of tokens) {
        if (result.slice(i, i + token.length) === token) {
          parts.push({ text: FORMAT_MAP[token], quoted: false });
          i += token.length;
          matched = true;
          break;
        }
      }
      if (!matched) {
        parts.push({ text: result[i], quoted: false });
        i++;
      }
    }
  }

  return parts.map((p) => p.text).join('');
};

/**
 * Convert a UTC ISO string to a wall-clock Date in a given timezone.
 * The returned Date's local fields (getFullYear, getMonth, etc.) represent
 * the wall-clock time in the target timezone.
 */
export const utcToPickerDate = (utcISO, timezone) => {
  if (!utcISO) return null;

  // Date only (e.g., DOB in YYYY-MM-DD format) — no timezone conversion
  if (typeof utcISO === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(utcISO)) {
    const [year, month, day] = utcISO.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Datetime with timezone conversion
  const zonedDate = toZonedTime(new Date(utcISO), timezone);
  return zonedDate;
};

/**
 * Convert a picker Date (representing wall-clock time in a timezone) to a UTC ISO string.
 */
export const pickerDateToUtc = (pickerDate, timezone) => {
  if (!pickerDate) return null;
  return fromZonedTime(pickerDate, timezone).toISOString();
};

/**
 * Get the user's local timezone via the Intl API.
 */
export const guessTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// ---------------------------------------------------------------------------
// Display formatting functions — all user-facing date rendering should use these
// ---------------------------------------------------------------------------

const DATE_ONLY_PATTERNS = [
  {
    re: /^\d{4}-\d{2}-\d{2}$/,
    parse: (s) => {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
  },
  {
    re: /^\d{1,2}\/\d{1,2}\/\d{4}$/,
    parse: (s) => {
      const [m, d, y] = s.split('/').map(Number);
      return new Date(y, m - 1, d);
    }
  },
  {
    re: /^\d{2}-\d{2}-\d{4}$/,
    parse: (s) => {
      const [m, d, y] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
  }
];

export const parseDateOnly = (value) => {
  if (typeof value !== 'string') return null;
  for (const { re, parse } of DATE_ONLY_PATTERNS) {
    if (re.test(value)) return parse(value);
  }
  return null;
};

const safeParse = (value) => {
  if (!value) return null;
  if (value instanceof Date) return dateFnsIsValid(value) ? value : null;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const dateOnly = parseDateOnly(value);
    if (dateOnly) return dateOnly;
    const parsed = parseISO(value);
    if (dateFnsIsValid(parsed)) return parsed;
    const fallback = new Date(value);
    if (dateFnsIsValid(fallback)) return fallback;
  }
  return null;
};

/**
 * Single formatting function for all date/time display.
 * Auto-detects date-only strings (YYYY-MM-DD, MM/DD/YYYY) and skips timezone conversion,
 * stripping time components from the format automatically.
 *
 * For datetime values the default produces e.g. '01/15/2024, 1:00 PM'.
 * For date-only values the time portion is stripped automatically so the
 * same default produces '01/15/2024'.
 *
 * When a timezone is provided the date is rendered in that timezone.
 *
 * @param {string|Date|number} date - The date value to format
 * @param {string} timezone - IANA timezone (e.g. 'America/New_York')
 * @param {string} [fmt='MM/dd/yyyy, h:mm a'] - date-fns format string
 * @returns {string} Formatted date string, or '' if invalid
 */
/** Format with timezone abbreviation — use where users may span timezones. */
export const LOCAL_DATETIME_FMT = 'MM/dd/yyyy, h:mm a zzz';

export const formatDate = (date, timezone, fmt = 'MM/dd/yyyy, h:mm a') => {
  if (!date) return '';
  const dateOnly = parseDateOnly(date);
  if (dateOnly) {
    // Strip time components from the format for date-only values.
    // A date-only value (e.g. DOB) should never display a time.
    const dateOnlyFmt =
      fmt
        .replace(/[\s,]*[hHkK]{1,2}:mm(?::ss)?\s*/g, '')
        .replace(/\s*a{1,4}\s*/g, '')
        .replace(/\s*z{1,4}\s*/g, '')
        .trim() || 'MM/dd/yyyy';
    return dateFnsFormat(dateOnly, dateOnlyFmt);
  }
  const d = safeParse(date);
  if (!d) return '';
  if (timezone) return formatInTimeZone(d, timezone, fmt);
  return dateFnsFormat(d, fmt);
};

// Re-export everything for convenience
export {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  dateFnsFormat as format,
  formatDistanceToNow,
  formatInTimeZone,
  fromZonedTime,
  getDate,
  getDay,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameYear,
  dateFnsIsValid as isValid,
  max,
  min,
  parse,
  parseISO,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subSeconds,
  subWeeks,
  subYears,
  toZonedTime
};
